import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { Loader2, Dna } from "lucide-react";

const LOCUS_ORDER = ["extension", "agouti", "cream", "dun", "gray", "toro", "overo", "lp", "sabino", "splash"];

// Genetics an in-game horse inherits from the admin-set genotype for its coat model.
export const HorseGenetics = ({ modelId }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!modelId) { setErr(true); return; }
    setD(null); setErr(false);
    gameApi.get(`/game/coat-genetics/${encodeURIComponent(modelId)}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [modelId]);

  if (err) return (
    <div className="mx-4 mt-3 rounded-2xl border border-border bg-card p-5 text-center text-sm text-muted-foreground" data-testid="horse-genetics-empty">
      Genetics aren't available for this horse's coat model yet.
    </div>
  );
  if (!d) return <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading genetics…</p>;

  const p = d.phenotype || {};
  const patterns = [p.gray && "Gray", p.tobiano && "Tobiano", p.roan && "Roan", p.overo && "Overo", p.lp && "Appaloosa", p.sabino && "Sabino", p.splash && "Splash White"].filter(Boolean);
  return (
    <div className="mx-4 mt-3 space-y-3" data-testid="horse-genetics-tab">
      <div className="rounded-2xl border border-border bg-card p-3">
        <p className="flex items-center gap-1.5 text-sm font-bold text-primary"><Dna size={15} /> Genetics</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Inherited from this horse's coat model{d.model_display ? ` — ${d.model_display}` : ""}.
          {!d.configured && " Showing default genes until an admin assigns this model's genotype."}
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-3" data-testid="horse-genetics-phenotype">
        <p className="font-display text-lg font-bold text-primary">{p.base_color || "—"}</p>
        <p className="text-[11px] text-muted-foreground">
          {p.base_family}{patterns.length ? ` · ${patterns.join(" + ")}` : " · Solid"}
          {p.cream_dose ? ` · Cream ×${p.cream_dose}` : ""}{p.dun ? " · Dun" : ""}
        </p>
        {p.display_note && <p className="mt-0.5 text-[11px] font-bold text-secondary">{p.display_note}</p>}
      </div>

      <div className="rounded-2xl border border-border bg-card p-3">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-secondary">Genotype · {LOCUS_ORDER.length} loci</p>
        <div className="grid grid-cols-2 gap-1.5 text-[11px]">
          {LOCUS_ORDER.map((l) => (
            <div key={l} className="flex items-center justify-between rounded-lg bg-muted/40 px-2 py-1.5" data-testid={`geno-${l}`}>
              <span className="truncate text-muted-foreground">{d.labels?.[l] || l}</span>
              <span className="ml-2 font-mono font-bold text-primary">{d.genotype?.[l] || "—"}</span>
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-[10px] text-muted-foreground">Two alleles per locus — one from each parent. Tobiano &amp; Roan share a single locus.</p>
      </div>
    </div>
  );
};
